<div class="docs-section" id="introduction">
  <div class="docs-section__head">
    <h1 class="docs-h1">Introduction</h1>
    <p class="docs-subtitle">The metered retrieval API for AI, built on the Cikisi index.</p>
  </div>

  <div class="docs-hero-actions">
    <a class="docs-btn docs-btn--primary" routerLink="/docs/quickstart">Quickstart <span aria-hidden="true">&rarr;</span></a>
    <a class="docs-btn" routerLink="/playground" target="_blank" rel="noopener">Open the Playground</a>
  </div>

  <p class="docs-subtitle">
    -mila. is a metered API over the Cikisi index, billed per call from your organization's prepaid wallet
    balance. A few things stay true on every request:
  </p>

  <ul class="docs-facts docs-facts--grid">
    <li class="docs-facts__item">
      <span class="docs-facts__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="m12 2 9 5-9 5-9-5 9-5Z"/>
          <path d="m3 12 9 5 9-5M3 17l9 5 9-5"/>
        </svg>
      </span>
      <div class="docs-facts__text">
        <p class="docs-facts__title">Simple</p>
        <p class="docs-facts__desc"><span class="docs-param">rag</span>, <span class="docs-param">answer</span>, <span class="docs-param">search</span>, <span class="docs-param">image</span> and <span class="docs-param">similar</span> share one request shape.</p>
      </div>
    </li>
    <li class="docs-facts__item">
      <span class="docs-facts__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
          <path d="M21 9h-6a3 3 0 0 0 0 6h6z"/>
        </svg>
      </span>
      <div class="docs-facts__text">
        <p class="docs-facts__title">Metered</p>
        <p class="docs-facts__desc">Billed per call from your wallet balance. See <a class="link" routerLink="/docs/pricing-limits">Pricing &amp; limits</a>.</p>
      </div>
    </li>
    <li class="docs-facts__item">
      <span class="docs-facts__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="8" cy="15" r="4"/>
          <path d="m10.85 12.15 8.65-8.65M18 5l2 2M15 8l2 2"/>
        </svg>
      </span>
      <div class="docs-facts__text">
        <p class="docs-facts__title">Authenticated</p>
        <p class="docs-facts__desc">Every request carries an <a class="link" routerLink="/api-keys">API key</a> tied to your org.</p>
      </div>
    </li>
    <li class="docs-facts__item">
      <span class="docs-facts__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="13" r="8"/>
          <path d="M12 9v4l2 2M9 2h6"/>
        </svg>
      </span>
      <div class="docs-facts__text">
        <p class="docs-facts__title">Rate-limited</p>
        <p class="docs-facts__desc">60 requests per minute per key keeps the index responsive.</p>
      </div>
    </li>
  </ul>
</div>

<div class="docs-section">
  <div class="docs-section__head">
    <h2 class="docs-h2" id="our-endpoints">Our endpoints</h2>
    <p class="docs-subtitle">
      Five endpoints, one request shape, tuned for what you need back: passages, prose, whole articles, similar articles or images.
    </p>
  </div>

  <div class="docs-ep-tiles">
    @for (ep of findEndpoints; track ep.id) {
      <a class="docs-ep-tile" [routerLink]="['/docs/api-reference', ep.id]">
        <span class="docs-ep-tile__head">
          <span class="endpoint-badge endpoint-badge--post">POST</span>
          <!-- Display-only trim of the '/api' prefix; ep.path stays the real
               path, used to build the code examples elsewhere. -->
          <span class="docs-ep-tile__path">{{ ep.path.replace('/api', '') }}</span>
          <span class="docs-ep-tile__arrow" aria-hidden="true">&rarr;</span>
        </span>
        <span class="docs-ep-tile__desc">{{ ep.tagline }}</span>
      </a>
    }
  </div>
</div>

<div class="docs-section">
  <div class="docs-section__head">
    <h2 class="docs-h2" id="how-it-works">How it works</h2>
  </div>
  <ol class="docs-timeline">
    <li><strong>Your app</strong> sends a request with your API key. See <a class="link" routerLink="/docs/authentication">Authentication</a>.</li>
    <li><strong>The -mila. API</strong> checks the key and wallet balance, then forwards the call to the index.</li>
    <li><strong>The Cikisi index</strong> runs the search. The match is relayed back with cost and balance in the response headers.</li>
  </ol>
</div>

<div class="docs-note">
  <span class="docs-note__icon" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m10 9 5 3-5 3V9Z"/>
    </svg>
  </span>
  <div class="docs-note__text">
    <p class="docs-note__title">Already have an account?</p>
    <p class="docs-note__desc">
      Run a real query against your own wallet in the <a class="link" routerLink="/playground" target="_blank" rel="noopener">Playground</a>:
      same endpoints, same billing, a form instead of a curl command.
    </p>
  </div>
</div>

<div class="docs-section">
  <div class="docs-section__head">
    <h2 class="docs-h2" id="where-to-next">Where to next</h2>
  </div>
  <div class="docs-next-grid">
    <a class="docs-next-card" routerLink="/docs/quickstart">
      <p class="docs-next-card__title">Quickstart <span aria-hidden="true">&rarr;</span></p>
      <p class="docs-next-card__desc">Make your first call in three steps.</p>
    </a>
    <a class="docs-next-card" routerLink="/docs/authentication">
      <p class="docs-next-card__title">Authentication <span aria-hidden="true">&rarr;</span></p>
      <p class="docs-next-card__desc">How your API key and wallet balance work together.</p>
    </a>
    <a class="docs-next-card" routerLink="/docs/api-reference">
      <p class="docs-next-card__title">API reference <span aria-hidden="true">&rarr;</span></p>
      <p class="docs-next-card__desc">Every parameter, response field and schema, per endpoint.</p>
    </a>
  </div>
</div>
